import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Link, useNavigate } from "react-router-dom";
import AdminNavbar from "../Layout/AdminNavbar";
import API_BASE_URL from "../config";

const INDUSTRIES = ["IT", "Construction", "Manufacturing", "Consulting", "Marketing", "Healthcare", "Finance", "Education", "Other"];
const CUSTOMER_TYPES = ["Individual", "Company", "Government", "NGO", "Other"];
const STATUSES = ["Active", "Inactive", "Blacklisted"];

const T = {
  primary: "#6366f1",
  primaryLight: "#eef2ff",
  primaryDark: "#4f46e5",
  surface: "#ffffff",
  surfaceAlt: "#f8fafc",
  border: "#e2e8f0",
  borderLight: "#f1f5f9",
  text: "#0f172a",
  textMuted: "#64748b",
  textLight: "#94a3b8",
  success: "#10b981",
  successBg: "#ecfdf5",
  danger: "#ef4444",
  dangerBg: "#fef2f2",
  warning: "#f59e0b",
  warningBg: "#fffbeb",
  radius: "10px",
  radiusSm: "6px",
  shadow: "0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
  shadowMd: "0 4px 12px rgba(99,102,241,0.10)",
};

const badge = (status) => {
  if (!status) return <span style={{ color: T.textLight }}>—</span>;
  const map = {
    Active:      { bg: T.successBg,  color: T.success,  dot: T.success },
    Inactive:    { bg: T.dangerBg,   color: T.danger,   dot: T.danger },
    Blacklisted: { bg: "#fdf2f8",    color: "#be185d",  dot: "#be185d" },
  };
  const s = map[status] || { bg: "#f1f5f9", color: T.textMuted, dot: T.textMuted };
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: s.bg, color: s.color, letterSpacing: 0.3, whiteSpace: "nowrap" }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: s.dot, flexShrink: 0 }} />
      {status}
    </span>
  );
};

const CustomerAvatar = ({ name }) => {
  const initials = (name || "")
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "")
    .join("") || "?";
  const colors = ["#6366f1","#8b5cf6","#ec4899","#06b6d4","#10b981","#f59e0b","#ef4444","#3b82f6"];
  const colorIdx = initials.charCodeAt(0) % colors.length;
  return (
    <div style={{ width: 32, height: 32, borderRadius: 8, background: colors[colorIdx], color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, letterSpacing: 0.5, flexShrink: 0 }}>
      {initials}
    </div>
  );
};

const Th = ({ label, sortKey, sortConfig, onSort }) => (
  <th
    onClick={() => sortKey && onSort(sortKey)}
    style={{
      position: "sticky", top: 0, background: "#f1f5f9", color: T.textMuted,
      fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.6,
      padding: "10px 12px", whiteSpace: "nowrap",
      cursor: sortKey ? "pointer" : "default",
      userSelect: "none", borderBottom: `2px solid ${T.border}`, zIndex: 10,
    }}
  >
    {label}
    {sortKey && (
      <span style={{ marginLeft: 4, opacity: sortConfig.key === sortKey ? 1 : 0.3 }}>
        {sortConfig.key === sortKey ? (sortConfig.direction === "asc" ? " ↑" : " ↓") : " ↕"}
      </span>
    )}
  </th>
);

const TdPlain = ({ children, style }) => (
  <td style={{ padding: "10px 12px", fontSize: 13, color: T.text, whiteSpace: "nowrap", ...style }}>
    {children}
  </td>
);

const fmt = (dateStr) =>
  dateStr ? new Date(dateStr).toLocaleDateString("en-GB") : <span style={{ color: T.textLight }}>—</span>;

const val = (v) => v || <span style={{ color: T.textLight }}>—</span>;

// ── Confirmation Modal ─────────────────────────────────────────────────────
const ConfirmModal = ({ show, title, message, confirmLabel, confirmVariant = "danger", onConfirm, onCancel }) => {
  if (!show) return null;
  const isWarn = confirmVariant === "warning";
  return (
    <>
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1050 }} onClick={onCancel} />
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)",
          zIndex: 1055, width: "100%", maxWidth: 420, background: T.surface,
          borderRadius: 12, boxShadow: "0 20px 60px rgba(0,0,0,0.18)", padding: "28px 28px 22px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
          <div style={{ width: 44, height: 44, borderRadius: 10, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: isWarn ? T.warningBg : T.dangerBg }}>
            <i className={`bi ${isWarn ? "bi-slash-circle" : "bi-trash3"}`} style={{ fontSize: 20, color: isWarn ? T.warning : T.danger }} />
          </div>
          <h6 style={{ margin: 0, fontWeight: 700, fontSize: 16, color: T.text }}>{title}</h6>
        </div>
        <p style={{ color: T.textMuted, fontSize: 14, lineHeight: 1.6, marginBottom: 24 }}>{message}</p>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button
            onClick={onConfirm}
            style={{
              padding: "7px 20px", borderRadius: T.radiusSm, border: "none",
              background: isWarn ? T.warning : T.danger, color: "#fff",
              fontWeight: 600, fontSize: 13, cursor: "pointer",
            }}
          >
            {confirmLabel}
          </button>
          <button
            onClick={onCancel}
            style={{
              padding: "7px 20px", borderRadius: T.radiusSm,
              border: `1.5px solid ${T.border}`, background: T.surface,
              color: T.textMuted, fontWeight: 600, fontSize: 13, cursor: "pointer",
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </>
  );
};

// ── Main Component ─────────────────────────────────────────────────────────
const AllCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [industryFilter, setIndustryFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [downloading, setDownloading] = useState(false);
  const [hoveredRow, setHoveredRow] = useState(null);

  const [modal, setModal] = useState({ show: false, type: null, customer: null });
  const openDeleteModal     = (c) => setModal({ show: true, type: "delete",     customer: c });
  const openDeactivateModal = (c) => setModal({ show: true, type: "deactivate", customer: c });
  const closeModal          = ()  => setModal({ show: false, type: null, customer: null });

  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  useEffect(() => {
    const roles = JSON.parse(localStorage.getItem("roles") || "[]");
    if (!token) { toast.error("You must be logged in."); navigate("/"); return; }
    if (!roles.includes("ROLE_ADMIN") && !roles.includes("ROLE_CUSTOMER_MANAGER")) {
      toast.error("Unauthorized."); navigate("/"); return;
    }
  }, [navigate, token]);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/customers/all`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((res) => { if (!res.ok) throw new Error("Failed to fetch customers"); return res.json(); })
      .then((data) => {
        const list = Array.isArray(data) ? data : data.content || [];
        setCustomers(list);
        setFilteredCustomers(list);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    let result = [...customers];
    if (search) {
      result = result.filter((c) =>
        c.customerName?.toLowerCase().includes(search.toLowerCase()) ||
        c.customerCode?.toLowerCase().includes(search.toLowerCase()) ||
        c.primaryContactEmail?.toLowerCase().includes(search.toLowerCase())
      );
    }
    if (statusFilter)   result = result.filter((c) => c.status === statusFilter);
    if (industryFilter) result = result.filter((c) => c.industry === industryFilter);
    if (typeFilter)     result = result.filter((c) => c.customerType === typeFilter);
    if (sortConfig.key) {
      result.sort((a, b) => {
        const vA = a[sortConfig.key] ?? "";
        const vB = b[sortConfig.key] ?? "";
        if (vA < vB) return sortConfig.direction === "asc" ? -1 : 1;
        if (vA > vB) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    setFilteredCustomers(result);
  }, [search, statusFilter, industryFilter, typeFilter, customers, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handleConfirm = async () => {
    const { type, customer } = modal;
    closeModal();

    if (type === "delete") {
      try {
        const res = await fetch(`${API_BASE_URL}/api/customers/delete/${customer.id}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("Cannot delete — may be linked to an active project.");
        toast.success("Customer deleted.");
        setCustomers((prev) => prev.filter((c) => c.id !== customer.id));
      } catch (err) {
        toast.error(err.message || "Failed to delete customer.");
      }
    }

    if (type === "deactivate") {
      try {
        const res = await fetch(`${API_BASE_URL}/api/customers/update/${customer.id}`, {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({ ...customer, status: "Inactive" }),
        });
        if (!res.ok) throw new Error();
        toast.success("Customer deactivated.");
        setCustomers((prev) =>
          prev.map((c) => c.id === customer.id ? { ...c, status: "Inactive" } : c)
        );
      } catch {
        toast.error("Failed to deactivate customer.");
      }
    }
  };

  const downloadReport = async (format) => {
    try {
      setDownloading(true);
      const params = new URLSearchParams({ format });
      if (statusFilter) params.append("status", statusFilter);
      const res = await fetch(`${API_BASE_URL}/api/reports/customers?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Customer_Report.${format === "pdf" ? "pdf" : "xlsx"}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded!");
    } catch {
      toast.error("Failed to download report.");
    } finally {
      setDownloading(false);
    }
  };

  const activeCount      = customers.filter((c) => c.status === "Active").length;
  const inactiveCount    = customers.filter((c) => c.status === "Inactive").length;
  const blacklistedCount = customers.filter((c) => c.status === "Blacklisted").length;

  const modalConfig = modal.type === "delete"
    ? { title: "Delete Customer", message: `Are you sure you want to permanently delete "${modal.customer?.customerName}"? This action cannot be undone.`, confirmLabel: "Delete", confirmVariant: "danger" }
    : { title: "Deactivate Customer", message: `Are you sure you want to deactivate "${modal.customer?.customerName}"? They will no longer appear as active.`, confirmLabel: "Deactivate", confirmVariant: "warning" };

  return (
    <>
      <ConfirmModal
        show={modal.show}
        title={modalConfig.title}
        message={modalConfig.message}
        confirmLabel={modalConfig.confirmLabel}
        confirmVariant={modalConfig.confirmVariant}
        onConfirm={handleConfirm}
        onCancel={closeModal}
      />

      <div className="fixed-top">
        <AdminNavbar />
      </div>

      <div style={{ paddingTop: 100, minHeight: "100vh", background: "#f8fafc", fontFamily: "'Inter', system-ui, sans-serif" }}>

        {/* ── Loading Spinner ── */}
        {loading && (
          <div style={{ position: "fixed", inset: 0, zIndex: 999, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "rgba(248,250,252,0.85)", backdropFilter: "blur(2px)" }}>
            <style>{`@keyframes cust-spin { to { transform: rotate(360deg); } }`}</style>
            <div style={{ width: 44, height: 44, border: `3px solid ${T.primaryLight}`, borderTopColor: T.primary, borderRadius: "50%", animation: "cust-spin 0.7s linear infinite" }} />
            <span style={{ marginTop: 14, fontSize: 13, color: T.textMuted, fontWeight: 500 }}>Loading customers…</span>
          </div>
        )}

        <div style={{ padding: "20px 24px" }}>

          {/* ── Header ── */}
          <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: "20px 24px", marginBottom: 20, display: "flex", justifyContent: "space-between", alignItems: "center", boxShadow: T.shadow }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                <div style={{ width: 36, height: 36, borderRadius: 8, background: T.primaryLight, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <i className="bi bi-building" style={{ color: T.primary, fontSize: 17 }} />
                </div>
                <h5 style={{ margin: 0, color: T.text, fontWeight: 700, fontSize: 18 }}>Customers</h5>
              </div>
              <div style={{ display: "flex", gap: 16, marginTop: 4, paddingLeft: 2 }}>
                <span style={{ color: T.textMuted, fontSize: 12 }}>
                  <span style={{ fontWeight: 700, fontSize: 15, marginRight: 4, color: T.text }}>{customers.length}</span>Total
                </span>
                <span style={{ color: T.textMuted, fontSize: 12 }}>
                  <span style={{ fontWeight: 700, fontSize: 15, marginRight: 4, color: T.success }}>{activeCount}</span>Active
                </span>
                <span style={{ color: T.textMuted, fontSize: 12 }}>
                  <span style={{ fontWeight: 700, fontSize: 15, marginRight: 4, color: T.danger }}>{inactiveCount}</span>Inactive
                </span>
                {blacklistedCount > 0 && (
                  <span style={{ color: T.textMuted, fontSize: 12 }}>
                    <span style={{ fontWeight: 700, fontSize: 15, marginRight: 4, color: "#be185d" }}>{blacklistedCount}</span>Blacklisted
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={() => navigate("/addcustomer")}
              style={{ background: T.primary, color: "#fff", border: "none", borderRadius: T.radiusSm, padding: "8px 16px", fontWeight: 600, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, boxShadow: "0 2px 8px rgba(99,102,241,0.25)" }}
            >
              <i className="bi bi-building-add" /> New Customer
            </button>
          </div>

          {/* ── Toolbar ── */}
          <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: "12px 16px", marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, boxShadow: T.shadow }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>

              {/* Search */}
              <div style={{ position: "relative" }}>
                <i className="bi bi-search" style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: T.textLight, fontSize: 13 }} />
                <input
                  type="text"
                  placeholder="Search name, code, email…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ paddingLeft: 30, paddingRight: 10, paddingTop: 7, paddingBottom: 7, border: `1.5px solid ${search ? T.primary : T.border}`, borderRadius: T.radiusSm, fontSize: 13, outline: "none", width: 210, color: T.text, transition: "border-color 0.15s", background: T.surface }}
                />
              </div>

              {/* Status */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{ padding: "7px 10px", border: `1.5px solid ${statusFilter ? T.primary : T.border}`, borderRadius: T.radiusSm, fontSize: 13, color: statusFilter ? T.text : T.textMuted, outline: "none", cursor: "pointer", background: T.surface }}
              >
                <option value="">All Statuses</option>
                {STATUSES.map((s) => <option key={s}>{s}</option>)}
              </select>

              {/* Industry */}
              <select
                value={industryFilter}
                onChange={(e) => setIndustryFilter(e.target.value)}
                style={{ padding: "7px 10px", border: `1.5px solid ${industryFilter ? T.primary : T.border}`, borderRadius: T.radiusSm, fontSize: 13, color: industryFilter ? T.text : T.textMuted, outline: "none", cursor: "pointer", background: T.surface }}
              >
                <option value="">All Industries</option>
                {INDUSTRIES.map((i) => <option key={i}>{i}</option>)}
              </select>

              {/* Type */}
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                style={{ padding: "7px 10px", border: `1.5px solid ${typeFilter ? T.primary : T.border}`, borderRadius: T.radiusSm, fontSize: 13, color: typeFilter ? T.text : T.textMuted, outline: "none", cursor: "pointer", background: T.surface }}
              >
                <option value="">All Types</option>
                {CUSTOMER_TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {/* Export Excel */}
              <button
                onClick={() => downloadReport("excel")}
                disabled={downloading}
                title="Download Excel report"
                style={{ padding: "7px 12px", border: `1.5px solid ${T.border}`, borderRadius: T.radiusSm, background: T.surface, color: T.textMuted, fontSize: 13, cursor: downloading ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 5, fontWeight: 500, opacity: downloading ? 0.6 : 1 }}
              >
                <i className="bi bi-file-earmark-excel" style={{ color: "#16a34a" }} />
                {downloading ? "Downloading…" : "Export"}
              </button>

              {/* Count pill */}
              <div style={{ padding: "6px 12px", background: T.primaryLight, color: T.primary, borderRadius: T.radiusSm, fontSize: 12, fontWeight: 600 }}>
                {filteredCustomers.length} result{filteredCustomers.length !== 1 ? "s" : ""}
              </div>
            </div>
          </div>

          {/* ── Table ── */}
          <div style={{ background: T.surface, borderRadius: T.radius, border: `1px solid ${T.border}`, boxShadow: T.shadow, overflow: "hidden" }}>
            <div style={{ maxHeight: "65vh", overflowY: "auto", overflowX: "auto", scrollbarWidth: "thin", scrollbarColor: "#cbd5e1 transparent" }}>
              <style>{`
                .cust-table::-webkit-scrollbar { width: 6px; height: 6px; }
                .cust-table::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
                .cust-table::-webkit-scrollbar-track { background: transparent; }
              `}</style>
              <table className="cust-table" style={{ width: "max-content", minWidth: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
                <thead>
                  <tr>
                    {/* Sticky actions column */}
                    <th style={{ position: "sticky", top: 0, left: 0, zIndex: 20, background: "#f1f5f9", padding: "10px 14px", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.6, color: T.textMuted, borderBottom: `2px solid ${T.border}`, whiteSpace: "nowrap" }}>
                      View | Edit
                    </th>
                    <Th label="Code"         sortKey="customerCode"  sortConfig={sortConfig} onSort={handleSort} />
                    <Th label="Customer"     sortKey="customerName"  sortConfig={sortConfig} onSort={handleSort} />
                    <Th label="Type"         sortKey="customerType"  sortConfig={sortConfig} onSort={handleSort} />
                    <Th label="Industry"     sortKey="industry"      sortConfig={sortConfig} onSort={handleSort} />
                    <Th label="Status"       sortKey="status"        sortConfig={sortConfig} onSort={handleSort} />
                    <Th label="Currency"     sortKey="currency"      sortConfig={sortConfig} onSort={handleSort} />
                    <Th label="Payment Terms" sortKey="paymentTerms" sortConfig={sortConfig} onSort={handleSort} />
                    <Th label="Created"      sortKey="createdAt"     sortConfig={sortConfig} onSort={handleSort} />
                    <Th label="Actions"      sortKey={null}          sortConfig={sortConfig} onSort={handleSort} />
                  </tr>
                </thead>
                <tbody>
                  {filteredCustomers.length > 0 ? (
                    filteredCustomers.map((c) => (
                      <tr
                        key={c.id}
                        onMouseEnter={() => setHoveredRow(c.id)}
                        onMouseLeave={() => setHoveredRow(null)}
                        style={{ background: hoveredRow === c.id ? "#f8faff" : T.surface, transition: "background 0.12s", borderBottom: `1px solid ${T.borderLight}` }}
                      >
                        {/* Sticky actions */}
                        <td style={{ position: "sticky", left: 0, background: hoveredRow === c.id ? "#f8faff" : T.surface, padding: "10px 14px", zIndex: 5, borderRight: `1px solid ${T.borderLight}`, whiteSpace: "nowrap" }}>
                          <Link to={`/viewcustomer/${c.id}`} style={{ fontSize: 12, fontWeight: 600, color: T.primary, textDecoration: "none", padding: "3px 8px", borderRadius: 4, background: T.primaryLight, marginRight: 6 }}>
                            View
                          </Link>
                          <Link to={`/editcustomer/${c.id}`} style={{ fontSize: 12, fontWeight: 600, color: "#64748b", textDecoration: "none", padding: "3px 8px", borderRadius: 4, background: "#f1f5f9" }}>
                            Edit
                          </Link>
                        </td>

                        {/* Code */}
                        <TdPlain>
                          <span style={{ background: T.primaryLight, color: T.primary, borderRadius: 4, padding: "2px 8px", fontFamily: "monospace", fontSize: 12, fontWeight: 600 }}>
                            {c.customerCode || "—"}
                          </span>
                        </TdPlain>

                        {/* Customer name with avatar */}
                        <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <CustomerAvatar name={c.customerName} />
                            <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{c.customerName || "—"}</span>
                          </div>
                        </td>

                        {/* Type */}
                        <TdPlain>
                          {c.customerType ? (
                            <span style={{ background: "#ede9fe", color: "#7c3aed", padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 600 }}>
                              {c.customerType}
                            </span>
                          ) : val(null)}
                        </TdPlain>

                        <TdPlain style={{ color: T.textMuted }}>{val(c.industry)}</TdPlain>
                        <TdPlain>{badge(c.status)}</TdPlain>
                        <TdPlain style={{ fontFamily: "monospace", fontSize: 12, fontWeight: 500 }}>{val(c.currency)}</TdPlain>
                        <TdPlain style={{ fontSize: 12 }}>{val(c.paymentTerms)}</TdPlain>
                        <TdPlain style={{ fontFamily: "monospace", fontSize: 12 }}>{fmt(c.createdAt)}</TdPlain>

                        {/* Action buttons */}
                        <TdPlain>
                          <div style={{ display: "flex", gap: 6 }}>
                            {c.status !== "Inactive" && (
                              <button
                                onClick={() => openDeactivateModal(c)}
                                title="Deactivate"
                                style={{ padding: "4px 10px", borderRadius: T.radiusSm, border: `1.5px solid ${T.warning}`, background: T.warningBg, color: T.warning, fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", gap: 4, fontWeight: 500 }}
                              >
                                <i className="bi bi-slash-circle" />
                              </button>
                            )}
                            <button
                              onClick={() => openDeleteModal(c)}
                              title="Delete"
                              style={{ padding: "4px 10px", borderRadius: T.radiusSm, border: `1.5px solid ${T.danger}`, background: T.dangerBg, color: T.danger, fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", gap: 4, fontWeight: 500 }}
                            >
                              <i className="bi bi-trash" />
                            </button>
                          </div>
                        </TdPlain>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={10} style={{ textAlign: "center", padding: "48px 20px", color: T.textLight }}>
                        <i className="bi bi-building" style={{ fontSize: 32, display: "block", marginBottom: 8 }} />
                        No customers found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {error && (
            <div style={{ marginTop: 12, padding: "10px 14px", background: T.dangerBg, color: T.danger, borderRadius: T.radiusSm, fontSize: 13 }}>
              {error}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AllCustomers;
